<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <div class="w-screen h-screen flex items-center justify-center">
        <loading-state v-if="!isLocal" />
        <template v-if="isAuthenticated">
          <p class="hidden">{{ getToken() }}</p>
        </template>
        <LocalLogin v-if="isLocal" :usernames="usernames" />
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage, isPlatform } from '@ionic/vue';
import axios from 'axios';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { defineComponent, computed, ref, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { mapActions } from 'vuex';
import { useRoute } from 'vue-router';
import { SplashScreen } from '@capacitor/splash-screen';
import LoadingState from '@/components/common/LoadingState.vue';
import LocalLogin from '@/components/Local/localLogin.vue';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { trackEvent } from '@/utils/mixpanel';

export default defineComponent({
  name: 'HomePage',
  components: {
    IonContent,
    IonPage,
    LoadingState,
    LocalLogin,
  },

  setup() {
    const { loginWithRedirect, isAuthenticated, user, checkSession } = useAuth0();
    const isBrowserOpen = ref(false);
    const isLoggingIn = ref(false);
    const isNative = isPlatform('android') || isPlatform('ios');
    const isAndroid = isPlatform('android');
    const isIos = isPlatform('ios');
    const isNativeBrowser = isPlatform('mobileweb');
    const isLocal = computed(() => process.env.VUE_APP_NODE_ENV === 'local');
    const usernames = ref([]);

    const login = async () => {
      // Guard 1: prevent concurrent loginWithRedirect calls
      // Guard 2: prevent opening a new browser while one is already open —
      //   calling Browser.open() while a Chrome Custom Tab is active forces
      //   the existing tab to close (firing browserFinished immediately),
      //   which triggers appStateChange(true), which triggers login() again,
      //   creating a rapid cascade of browser open/close events.
      if (isLoggingIn.value || isBrowserOpen.value) {
        return;
      }
      isLoggingIn.value = true;
      // NOT removed here: iOS browserFinished re-runs login() after the user
      // taps Done, and that retry must still carry the deep link. The entry is
      // cleared at CONSUMPTION — after a successful auth callback in App.vue —
      // so it can't replay on later re-logins.
      let deepLinkPathObj = null;
      try {
        deepLinkPathObj = JSON.parse(sessionStorage.getItem('deepLinkPathObj'));
      } catch {
        // Malformed entry must never brick the Login button — drop it.
        sessionStorage.removeItem('deepLinkPathObj');
      }
      console.log('login - deepLinkPathObj in session:', deepLinkPathObj);
      try {
        await loginWithRedirect({
          appState: {
            fullPathObj: deepLinkPathObj,
          },
          openUrl: url => Browser.open({ url, windowName: '_self' }),
        });
        // Set isBrowserOpen after loginWithRedirect resolves so we only mark
        // the browser as open once we know the call succeeded.
        isBrowserOpen.value = true;
      } finally {
        // Always reset isLoggingIn so future login() calls are not blocked
        // if loginWithRedirect fails or the browser never opened.
        isLoggingIn.value = false;
      }
    };

    function delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    if (isNative && !isNativeBrowser) {
      FirebaseAnalytics.logEvent({
        name: 'native_login',
      });
      trackEvent('Native Login');
    }

    // iOS only: re-open the browser if the user taps "Done" to dismiss it.
    // On iOS, SFSafariViewController stays visible when the user switches apps
    // (it comes back with the app), so browserFinished only fires on explicit
    // dismissal — the watcher is safe here with no cascade risk.
    if (isIos && !isNativeBrowser) {
      watch(isBrowserOpen, async value => {
        if (!value) {
          await delay(1000);
          if (!isAuthenticated.value) {
            await login();
          }
        }
      });
    }

    return {
      isAuthenticated,
      user,
      login,
      isBrowserOpen,
      isLoggingIn,
      delay,
      isNative,
      isAndroid,
      isIos,
      isNativeBrowser,
      checkSession,
      isLocal,
      usernames,
    };
  },
  data() {
    return {
      token: '',
    };
  },
  async mounted() {
    if (this.isLocal) {
      const { data: usernames } = await axios.get(process.env.VUE_APP_API_URL + '/local/usernames');
      this.usernames = usernames;
    }
    // Auto-login moved to ionViewWillEnter so it re-triggers when navigating
    // back to this cached page (e.g. from privacy-policy). mounted() only fires
    // on first instantiation, so it can't re-open the Auth0 popup on return.
  },
  // Ionic fires this every time the view becomes active — first entry AND every
  // return to it — unlike mounted/created which only run once.
  async ionViewWillEnter() {
    // Auto-login runs here (not mounted) so it re-triggers when navigating back to
    // this cached page (e.g. from privacy-policy); mounted only fires on first mount.
    // Entering the login view means no Auth0 browser is in the foreground.
    // Reset the guard flag: `browserFinished` (which normally clears it) does NOT
    // fire when the browser is closed programmatically — e.g. the deep-link
    // handler's Browser.close() in App.vue — so isBrowserOpen can get stuck `true`
    // and block every subsequent login(). This is safe because ionViewWillEnter
    // only fires on real navigation into the page, never over a live browser.
    this.isBrowserOpen = false;
    if (this.isLocal) {
      return;
    }
    if (!this.isAuthenticated) {
      await this.login();
    }
  },
  async created() {
    Browser.addListener('browserFinished', () => {
      this.isBrowserOpen = false;
    });
    // Android only: Chrome Custom Tab becomes a separate background task when
    // the user switches apps, so the browser is no longer visible when they
    // return to the app. appStateChange(true) is the correct hook to re-open it.
    // On iOS this is NOT needed — SFSafariViewController stays attached to the
    // app and reappears automatically; adding this handler on iOS would stack
    // a second browser on top of the still-visible one.
    if (this.isAndroid && !this.isNativeBrowser) {
      CapApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && !this.isAuthenticated) {
          await this.login();
        }
      });
    }
    await SplashScreen.hide();
  },
  methods: {
    ...mapActions('user', ['setToken']),
    async getToken() {
      console.log('presignin getToken');
      const route = useRoute();
      // console.log(route)
      const path = computed(() => route.path);
      if (process.env.VUE_APP_NODE_ENV === 'local') {
        this.token = localStorage.getItem('token');
      } else {
        const { getAccessTokenSilently } = useAuth0();
        this.token = await getAccessTokenSilently();
      }
      if (path.value.includes('login')) {
        console.log('pushing to home here');
        await this.setToken(this.token);
        const currentConference = await this.$store.dispatch('profile/fetchCurrentConference');
        console.log('[Login] fetchCurrentConference result:', currentConference);
        const type = currentConference?.type;
        if (type === 'GIC') {
          console.log('[Login] routing to /gic/home');
          this.$router.push('/gic/home');
        } else if (type === 'GPCR') {
          console.log('[Login] routing to /gpcr/home');
          this.$router.push('/gpcr/home');
        } else {
          console.log('[Login] routing to /home');
          this.$router.push('/home');
        }
      }
      if (path.value.includes('pre-sign-in')) {
        console.log('has token and going to pre-sign-in. Pushing to home');
        this.$router.push('/home');
      } else {
        console.log('else path value: ' + path.value);
      }
      this.setToken(this.token);
    },
  },
});
</script>

<style scoped>
#container {
  text-align: center;

  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

#container strong {
  font-size: 20px;
  line-height: 26px;
}

#container p {
  font-size: 16px;
  line-height: 22px;

  color: #8c8c8c;

  margin: 0;
}

#container a {
  text-decoration: none;
}
</style>
